import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  FaTimes, 
  FaMapMarkerAlt, 
  FaUser, 
  FaPhone,
  FaTruck,
  FaBox,
  FaCalendar,
  FaMoneyBillWave,
  FaCheck,
  FaRoute,
  FaBell,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import { updateOrder } from '../../../../store/actions/orders';
import axios from '../../../../lib/axios';
import Container from './styles';

const OrderDetails = ({ showModal, onClose, order }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deliveryFee, setDeliveryFee] = useState(order?.deliveryFee || order?.delivery_fee || '');
  const [updating, setUpdating] = useState(false);
  const [webhookTriggers, setWebhookTriggers] = useState([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);

  // Fetch webhook triggers when order changes
  useEffect(() => {
    if (order && order.id) {
      fetchWebhookTriggers();
    }
  }, [order?.id]);

  const fetchWebhookTriggers = async () => {
    setLoadingWebhooks(true);
    try {
      const response = await axios.get(`/company/orders/${order.id}/webhook-triggers`);
      if (response.data.status === 'success') {
        setWebhookTriggers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching webhook triggers:', error);
      setWebhookTriggers([]);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  if (!showModal || !order) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA726',
      assigned: '#42A5F5',
      picked_up: '#7E57C2',
      in_transit: '#66BB6A',
      delivered: '#4CAF50',
      cancelled: '#EF5350',
    };
    return colors[status] || '#9E9E9E';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStatus = (status) => {
    return status?.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || 'Unknown';
  };

  const handleUpdateFeeAndActivate = async () => {
    if (!deliveryFee || parseFloat(deliveryFee) <= 0) {
      alert('Please enter a valid delivery fee');
      return;
    }

    setUpdating(true);
    try {
      const updateData = {
        deliveryFee: parseFloat(deliveryFee),
        status: 'ASSIGNED' // Activate the order
      };

      const result = await dispatch(updateOrder(order.id, updateData));
      if (result && result.success) {
        alert('Order updated and activated successfully!');
        onClose();
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order');
    } finally {
      setUpdating(false);
    }
  };

  // Helper function to get field value (supports both camelCase and snake_case)
  const getField = (field1, field2) => order[field1] || order[field2];

  const handleTrackRider = () => {
    const trackingNumber = getField('trackingNumber', 'tracking_number');
    if (trackingNumber) {
      // Open tracking page in new tab
      window.open(`/track-order/${trackingNumber}`, '_blank');
    } else {
      alert('Tracking number not available');
    }
  };

  const canTrackRider = () => {
    const status = order.status?.toUpperCase();
    const hasRider = getField('riderId', 'rider_id');
    // Can track if order is accepted, picked up, or in transit and has assigned rider
    return hasRider && ['ACCEPTED', 'PICKED_UP', 'IN_TRANSIT'].includes(status);
  };

  return (
    <Container onClick={onClose}>
      <div className="modal--content" onClick={(e) => e.stopPropagation()}>
        <div className="modal--header">
          <div>
            <h2>Order Details</h2>
            <p className="tracking--number">{getField('trackingNumber', 'tracking_number')}</p>
          </div>
          <button className="close--btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal--body">
          <div className="status--section">
            <div className="status--badge" style={{ background: getStatusColor(order.status) }}>
              {formatStatus(order.status)}
            </div>
          </div>

          {order.status === 'PENDING' && (
            <div className="section fee--section">
              <h3 className="section--title">
                <FaMoneyBillWave className="icon" />
                Set Delivery Fee & Activate Order
              </h3>
              <div className="fee--input-group">
                <div className="input--wrapper">
                  <label>Delivery Fee (₦)</label>
                  <input
                    type="number"
                    value={deliveryFee}
                    onChange={(e) => setDeliveryFee(e.target.value)}
                    placeholder="Enter delivery fee"
                    min="0"
                    step="0.01"
                  />
                </div>
                <button 
                  className="btn btn--primary btn--activate"
                  onClick={handleUpdateFeeAndActivate}
                  disabled={updating || !deliveryFee}
                >
                  <FaCheck className="icon" />
                  {updating ? 'Updating...' : 'Set Fee & Activate'}
                </button>
              </div>
            </div>
          )}

          <div className="section">
            <h3 className="section--title">
              <FaUser className="icon" />
              Customer Information
            </h3>
            <div className="info--grid">
              <div className="info--item">
                <span className="label">Name</span>
                <span className="value">{getField('customerName', 'customer_name') || 'N/A'}</span>
              </div>
              <div className="info--item">
                <span className="label">Phone</span>
                <span className="value">
                  <FaPhone className="small-icon" />
                  {getField('customerPhone', 'customer_phone') || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="section">
            <h3 className="section--title">
              <FaMapMarkerAlt className="icon" />
              Locations
            </h3>
            <div className="locations--container">
              <div className="location--item pickup">
                <div className="location--header">
                  <FaMapMarkerAlt className="icon" />
                  <span>Pickup Location</span>
                </div>
                <p className="address">{getField('pickupAddress', 'pickup_address') || 'N/A'}</p>
              </div>

              <div className="location--item delivery">
                <div className="location--header">
                  <FaMapMarkerAlt className="icon" />
                  <span>Delivery Location</span>
                </div>
                <p className="address">{getField('deliveryAddress', 'delivery_address') || 'N/A'}</p>
              </div>
            </div>
          </div>

          {(getField('assignedRiderName', 'assigned_rider_name') || getField('riderId', 'rider_id')) && (
            <div className="section">
              <h3 className="section--title">
                <FaTruck className="icon" />
                Assigned Rider
              </h3>
              <div className="info--grid">
                <div className="info--item">
                  <span className="label">Rider Name</span>
                  <span className="value">{getField('assignedRiderName', 'assigned_rider_name') || 'Assigned'}</span>
                </div>
              </div>
              
              {canTrackRider() && (
                <button 
                  className="btn btn--track-rider"
                  onClick={handleTrackRider}
                >
                  <FaRoute className="icon" />
                  Track Rider Live
                </button>
              )}
            </div>
          )}

          <div className="section">
            <h3 className="section--title">
              <FaBox className="icon" />
              Package Details
            </h3>
            <div className="info--grid">
              <div className="info--item">
                <span className="label">Package Size</span>
                <span className="value">{getField('packageSize', 'package_size') || 'N/A'}</span>
              </div>
              <div className="info--item">
                <span className="label">Weight</span>
                <span className="value">
                  {getField('packageWeight', 'package_weight') ? `${getField('packageWeight', 'package_weight')} kg` : 'N/A'}
                </span>
              </div>
              <div className="info--item full-width">
                <span className="label">Description</span>
                <span className="value">{getField('packageDescription', 'package_description') || 'No description'}</span>
              </div>
            </div>
          </div>

          <div className="section">
            <h3 className="section--title">
              <FaMoneyBillWave className="icon" />
              Payment Information
            </h3>
            <div className="info--grid">
              <div className="info--item">
                <span className="label">Delivery Fee</span>
                <span className="value price">
                  ₦{(getField('deliveryFee', 'delivery_fee') || 0).toFixed(2)}
                </span>
              </div>
              <div className="info--item">
                <span className="label">Payment Status</span>
                <span className={`value payment--status ${(getField('paymentStatus', 'payment_status') || '').toLowerCase()}`}>
                  {(getField('paymentStatus', 'payment_status') || 'PENDING').toUpperCase()}
                </span>
              </div>
              {getField('paymentMethod', 'payment_method') && (
                <div className="info--item">
                  <span className="label">Payment Method</span>
                  <span className="value">{getField('paymentMethod', 'payment_method')}</span>
                </div>
              )}
            </div>
          </div>

          <div className="section">
            <h3 className="section--title">
              <FaCalendar className="icon" />
              Timeline
            </h3>
            <div className="info--grid">
              <div className="info--item">
                <span className="label">Created</span>
                <span className="value">{formatDate(getField('createdAt', 'created_at'))}</span>
              </div>
              {getField('updatedAt', 'updated_at') && (
                <div className="info--item">
                  <span className="label">Last Updated</span>
                  <span className="value">{formatDate(getField('updatedAt', 'updated_at'))}</span>
                </div>
              )}
            </div>
          </div>

          {getField('notes', 'notes') && (
            <div className="section">
              <h3 className="section--title">Notes</h3>
              <p className="notes">{getField('notes', 'notes')}</p>
            </div>
          )}

          {/* Webhook History Section */}
          <div className="section">
            <h3 className="section--title">
              <FaBell className="icon" />
              Webhook History {webhookTriggers.length > 0 && `(${webhookTriggers.length})`}
            </h3>
            {loadingWebhooks ? (
              <p className="loading--text">Loading webhook history...</p>
            ) : webhookTriggers.length === 0 ? (
              <p className="empty--text">No webhook triggers for this order yet</p>
            ) : (
              <div className="webhook--triggers">
                {webhookTriggers.map((trigger) => (
                  <div key={trigger.id} className={`webhook--trigger ${trigger.success ? 'success' : 'failed'}`}>
                    <div className="trigger--header">
                      <div className="trigger--event">
                        {trigger.success ? (
                          <FaCheckCircle className="icon success" />
                        ) : (
                          <FaTimesCircle className="icon failed" />
                        )}
                        <span className="event--name">{trigger.event}</span>
                      </div>
                      <span className="trigger--time">
                        {new Date(trigger.triggeredAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="trigger--details">
                      <div className="detail--row">
                        <span className="detail--label">URL:</span>
                        <span className="detail--value url">{trigger.url}</span>
                      </div>
                      {trigger.httpStatus && (
                        <div className="detail--row">
                          <span className="detail--label">HTTP Status:</span>
                          <span className={`detail--value status--${Math.floor(trigger.httpStatus / 100)}`}>
                            {trigger.httpStatus}
                          </span>
                        </div>
                      )}
                      {trigger.errorMessage && (
                        <div className="detail--row error">
                          <span className="detail--label">Error:</span>
                          <span className="detail--value">{trigger.errorMessage}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal--footer">
          <button className="btn btn--secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Container>
  );
};

export default OrderDetails;
